import { css } from '@emotion/react';
import {
  type FC, useCallback, useMemo,
} from 'react';
import { MetricsDataAction } from '~/_shared/utils/metric/metrics.types';
import { useGetMetricRegion } from '~/_shared/utils/metric/useGetMetricRegion';
import { useTranslation } from '../../../utils/hooks';
import { SelectedItemComponent } from '../../selectedItem/selectedItem.component';
import { getDataActionShortLabels } from '../customizeMetrics.helpers';
import {
  type SelectedDataMetric, SelectedDataType,
} from '../selectedDataMetric.type';

type ResultsSectionProps = Readonly<{
  selectedData: ReadonlyArray<SelectedDataMetric>;
  onRemoveItem: (index: number) => void;
}>;

const noDataMessageStyle = css({
  margin: '0 0 5px',
});

const selectedItemsContainerStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
});

const selectedItemStyle = css({
  margin: '0 10px 10px 0',
});

const containerStyle = css({
  padding: '10px 16px 0',
  marginBottom: -15,
});

type MetricDisplayData = {
  dataName: string;
  dataActionLabel: string | null;
  valueName: string;
};

const getMetricDisplayData = (
  data: SelectedDataMetric,
  dataActionLabels: Record<MetricsDataAction, string>,
): MetricDisplayData => {
  if (data.type === SelectedDataType.Demographic) {
    return {
      dataName: data.selectedDemographic.name,
      dataActionLabel: null,
      valueName: '',
    };
  }

  return {
    dataName: data.primarySpreadsheetColumn.name,
    dataActionLabel: dataActionLabels[data.dataAction],
    valueName: data.dataAction === MetricsDataAction.GROUP_TOTAL ? data.secondarySpreadsheetColumn.name : '',
  };
};

const getMetricSuffix = (dataActionLabel: string | null, valueName: string, metricRegion: string | null): string => {
  let suffix = dataActionLabel ? `(${dataActionLabel})` : '';

  if (valueName) {
    suffix = suffix ? `${suffix} ${valueName}` : valueName;
  }

  if (metricRegion) {
    suffix = suffix ? `${suffix} ${metricRegion}` : metricRegion;
  }

  return suffix;
};

export const CustomizeMetricsResultsComponent: FC<ResultsSectionProps> = (props) => {
  const [t] = useTranslation();
  const { getMetricRegion } = useGetMetricRegion();
  const dataActionLabels = useMemo(() => getDataActionShortLabels(t), [t]);
  const { selectedData } = props;

  const renderMetricRegion = useCallback((selectedMetric: SelectedDataMetric) => {
    if (selectedMetric.type === SelectedDataType.Demographic) {
      const metricRegion = getMetricRegion(selectedMetric.selectedDemographic.id);
      if (metricRegion) {
        return `(${metricRegion})`;
      }
    }

    return null;
  }, [getMetricRegion]);

  return (
    <div css={containerStyle}>
      {selectedData.length === 0 ?
        <p css={noDataMessageStyle}>{t('No data sources present.')}</p>
        : (
          <div css={selectedItemsContainerStyle}>
            {selectedData.map((data, index) => {
              const { dataName, dataActionLabel, valueName } = getMetricDisplayData(data, dataActionLabels);
              const metricSuffix = getMetricSuffix(dataActionLabel, valueName, renderMetricRegion(data));

              return (
                <SelectedItemComponent
                  css={selectedItemStyle}
                  onRemove={() => props.onRemoveItem(index)}
                  key={index + dataName + dataActionLabel + valueName}
                >
                  {dataName} {metricSuffix}
                </SelectedItemComponent>
              );
            })}
          </div>
        )}
    </div>
  );
};
